import { atom } from 'jotai'
import { atomFamily, atomWithStorage } from 'jotai/utils'

import {
  DISPATCH_DETAIL_FRAME_TYPE,
  type DispatchDetailFrameType
} from '@/libs/api/types'

/** 前回色を覚える枠の種類（配送の矢羽根は背景色を持たないため対象外） */
export const LAST_FRAME_COLOR_TYPES = [
  DISPATCH_DETAIL_FRAME_TYPE.SCHEDULED,
  DISPATCH_DETAIL_FRAME_TYPE.FREE_TIME
] as const satisfies readonly DispatchDetailFrameType[]
export type LastFrameColorType = (typeof LAST_FRAME_COLOR_TYPES)[number]

/** 枠の種類ごとの前回選んだ背景色（HEX 大文字）。未保存の種類は持たない */
export type LastFrameColors = Partial<Record<LastFrameColorType, string>>

export const HEX_COLOR_PATTERN = /^#[0-9A-F]{6}$/

/** 保存値が壊れている場合（手で書き換えた、旧版の値が残っている）は、壊れた種類だけ捨てる */
const normalize = (value: unknown): LastFrameColors => {
  if (typeof value !== 'object' || value === null) return {}

  const record = value as Record<string, unknown>
  const result: LastFrameColors = {}
  for (const frameType of LAST_FRAME_COLOR_TYPES) {
    const color = record[frameType]
    if (typeof color === 'string' && HEX_COLOR_PATTERN.test(color)) {
      result[frameType] = color
    }
  }
  return result
}

type Params = { userId: number }

/**
 * 枠の種類ごとに 1 色ずつ、ユーザー単位で localStorage に持つ Atom を作る
 * - 前回色（lastFrameColorAtomFamily）と既定色（frameDefaultColorAtomFamily）で共用する
 * - 書き込みは { frameType, frameColor }。HEX は大文字に揃えて保存する
 */
export const createFrameColorByTypeAtomFamily = (storageKeyPrefix: string) =>
  atomFamily(
    (params: Params) => {
      const storageKey = `${storageKeyPrefix}:${String(params.userId)}`

      // getOnInit: true — 初回描画から保存値を使う。false（既定）だとマウントまで既定値のままとなり、
      // フォームの defaultValues が既定色で固まった後に保存値が届いてしまう
      const base = atomWithStorage<LastFrameColors>(storageKey, {}, undefined, {
        getOnInit: true
      })

      return atom(
        (get) => normalize(get(base)),
        (
          get,
          set,
          next: { frameType: LastFrameColorType; frameColor: string }
        ) =>
          set(base, {
            ...normalize(get(base)),
            [next.frameType]: next.frameColor.toUpperCase()
          })
      )
    },
    (a, b) => a.userId === b.userId
  )

/**
 * 予定枠・空き時間枠を作る時の背景色の前回値 Atom（ユーザー単位）
 * - 枠の種類ごとに、最後に保存した背景色を覚える。次に作る時の初期値になる
 * - localStorage で永続化（別 PC・別ブラウザでは種類ごとの既定色 DEFAULT_FRAME_COLOR_BY_TYPE から始まる）
 * - ColorPicker のパレットは HEX 一致で選択表示するため、保存値がパレット色ならその色が選択された状態で開く
 */
export const lastFrameColorAtomFamily = createFrameColorByTypeAtomFamily(
  'dispatch-frame-last-color'
)
